import useUrlState from 'Hooks/useUrlState';
import { ExpandedState } from '@tanstack/react-table';
import { useMemo } from 'react';
import PublicReportInstagramPostsTable from '../../Posts/InstagramPosts/PublicReportInstagramPostsTable';
import InstagramPublicReportCampaignTable from '../CampaignsTable/InstagramPublicReportCampaignTable';
import { useCopyReportCampaignPostUrls } from 'Modules/Plans/Reports/Report/hooks/useCopyReportCampaignPostUrls';
import { PostUrlClipboardButton } from 'Modules/Plans/CopyToClipboard/PostUrlTable/Button/PostUrlClipboardButton';
import { useInstagramCampaigns } from './useInstagramCampaigns';
import styles from '../PublicReportCampaigns.module.css';

type Props = {
    publicReportId: string | undefined;
};

type UrlState = Partial<{
    expanded: string;
}>;

const ReportInstagramCampaigns = ({ publicReportId }: Props) => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const {
        rows,
        isCampaignsLoading,
        isCampaignsError,
        isProjectsLoading,
        isTeamsLoading,
        isBrandLoading,
        isCampaignStatsLoading,
        isTimeSeriesLoading,
        isTimeSeriesError,
    } = useInstagramCampaigns();

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;

        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const { isLoading: isCopyingPostUrls, copyReportCampaignPostUrls } = useCopyReportCampaignPostUrls(
        'instagram',
        rows,
        publicReportId
    );

    return (
        <>
            <div className={styles.actions}>
                <PostUrlClipboardButton
                    onClick={() => copyReportCampaignPostUrls()}
                    disabled={!rows.length}
                    isLoading={isCopyingPostUrls}
                />
            </div>
            <InstagramPublicReportCampaignTable
                data={rows}
                isLoading={isCampaignsLoading}
                hasError={isCampaignsError}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                meta={{
                    isLoading: isCampaignsLoading,
                    getIsProjectLoading: () => isProjectsLoading,
                    getIsTeamLoading: () => isTeamsLoading,
                    getIsBrandLoading: (row) => isBrandLoading(row.id),
                    getIsCampaignStatsLoading: (row) => isCampaignStatsLoading(row.id),
                    getIsTimeSeriesLoading: () => isTimeSeriesLoading,
                    getTimeSeriesHasError: () => isTimeSeriesError,
                }}
                renderSubComponent={(row) => <PublicReportInstagramPostsTable campaign={row.original} />}
            />
        </>
    );
};

export default ReportInstagramCampaigns;
